
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { Home, Camera, User as UserIcon, ShieldAlert, BookOpen, Beaker, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { useDarkroomMode } from "@/hooks/useDarkroomMode";

interface MobileNavBottomProps {
  user: User | null;
  isAdmin: boolean;
}

export function MobileNavBottom({ user, isAdmin }: MobileNavBottomProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { canNavigateAway, darkroomMode } = useDarkroomMode();
  
  // Helper function to handle navigation with darkroom check
  const handleNavigation = (path: string) => (e: React.MouseEvent) => {
    if (!canNavigateAway(path)) {
      e.preventDefault();
      return;
    }
  };
  
  // Determine if we're in darkroom mode and on the develop page
  const isDarkroomActive = darkroomMode && location.pathname === '/develop';
  
  // Calculate the number of columns based on user status and admin status
  const getGridColumns = () => {
    if (!user) return "grid-cols-4"; // Not logged in: Home, Resources, Sign In, Sign Up
    if (isAdmin) return "grid-cols-6"; // Admin: Home, Inventory, Resources, Develop, Profile, Admin - Now with 6 columns for 6 items
    return "grid-cols-5"; // Regular user: Home, Inventory, Resources, Develop, Profile
  };
  
  return (
    <div className={cn(
      "fixed bottom-0 left-0 z-50 h-16 w-full border-t shadow-md",
      isDarkroomActive 
        ? "!bg-black border-[#481218]" // Added ! to force black background in darkroom mode
        : "bg-white dark:bg-gray-900" // Explicitly set to white in light mode and dark gray in dark mode
    )}>
      <div className="mx-auto grid h-full max-w-lg font-medium">
        <div className={cn(
          "grid h-full", 
          getGridColumns()
        )}>
          {/* Home tab always shown */}
          <Link
            to="/"
            onClick={handleNavigation('/')}
            className={cn(
              "group inline-flex flex-col items-center justify-center px-1",
              isDarkroomActive 
                ? "hover:bg-black" 
                : "hover:bg-gray-50 dark:hover:bg-gray-800",
              location.pathname === '/' && !isDarkroomActive && "text-primary"
            )}
          >
            <Home className={cn(
              "mb-1 h-5 w-5",
              isDarkroomActive 
                ? "text-[#ea384c]" 
                : location.pathname === '/' 
                  ? "text-primary" 
                  : "text-gray-500 group-hover:text-primary"
            )} />
            <span className={cn(
              "text-xs truncate",
              isDarkroomActive 
                ? "text-[#ea384c]" 
                : location.pathname === '/' 
                  ? "text-primary" 
                  : "text-gray-500 group-hover:text-primary"
            )}>
              {t('home')}
            </span>
          </Link>
          
          {/* Inventory tab for logged in users */}
          {user && (
            <Link
              to="/inventory"
              onClick={handleNavigation('/inventory')}
              className={cn(
                "group inline-flex flex-col items-center justify-center px-1",
                isDarkroomActive 
                  ? "hover:bg-black" 
                  : "hover:bg-gray-50 dark:hover:bg-gray-800",
                location.pathname === '/inventory' && !isDarkroomActive && "text-primary"
              )}
            >
              <Camera className={cn(
                "mb-1 h-5 w-5",
                isDarkroomActive 
                  ? "text-[#ea384c]" 
                  : location.pathname === '/inventory' 
                    ? "text-primary" 
                    : "text-gray-500 group-hover:text-primary"
              )} />
              <span className={cn(
                "text-xs truncate",
                isDarkroomActive 
                  ? "text-[#ea384c]" 
                  : location.pathname === '/inventory' 
                    ? "text-primary" 
                    : "text-gray-500 group-hover:text-primary"
              )}>
                {t('inventory')}
              </span>
            </Link>
          )}
          
          {/* Resource tab always shown now (for both logged in and non-logged in users) */}
          <Link
            to="/resources"
            onClick={handleNavigation('/resources')}
            className={cn(
              "group inline-flex flex-col items-center justify-center px-1",
              isDarkroomActive 
                ? "hover:bg-black" 
                : "hover:bg-gray-50 dark:hover:bg-gray-800",
              location.pathname === '/resources' && !isDarkroomActive && "text-primary"
            )}
          >
            <BookOpen className={cn(
              "mb-1 h-5 w-5",
              isDarkroomActive 
                ? "text-[#ea384c]" 
                : location.pathname === '/resources' 
                  ? "text-primary" 
                  : "text-gray-500 group-hover:text-primary"
            )} />
            <span className={cn(
              "text-xs truncate",
              isDarkroomActive 
                ? "text-[#ea384c]" 
                : location.pathname === '/resources' 
                  ? "text-primary" 
                  : "text-gray-500 group-hover:text-primary"
            )}>
              {t('resources')}
            </span>
          </Link>
          
          {/* Develop tab for logged in users or Sign Up for non-logged in users */}
          {user ? (
            <Link
              to="/develop"
              onClick={handleNavigation('/develop')}
              className={cn(
                "group inline-flex flex-col items-center justify-center px-1",
                isDarkroomActive 
                  ? "hover:bg-black" 
                  : "hover:bg-gray-50 dark:hover:bg-gray-800",
                (location.pathname === '/develop' && !isDarkroomActive) 
                  ? "text-primary" 
                  : isDarkroomActive && "text-[#ea384c]"
              )}
            >
              <Beaker className={cn(
                "mb-1 h-5 w-5",
                isDarkroomActive 
                  ? "text-[#ea384c]" 
                  : location.pathname === '/develop' 
                    ? "text-primary" 
                    : "text-gray-500 group-hover:text-primary"
              )} />
              <span className={cn(
                "text-xs truncate",
                isDarkroomActive 
                  ? "text-[#ea384c]" 
                  : location.pathname === '/develop' 
                    ? "text-primary" 
                    : "text-gray-500 group-hover:text-primary"
              )}>
                {t('develop')}
              </span>
            </Link>
          ) : (
            <Link
              to="/auth?signup=true"
              onClick={handleNavigation('/auth?signup=true')}
              className={cn(
                "group inline-flex flex-col items-center justify-center px-1",
                isDarkroomActive 
                  ? "hover:bg-black" 
                  : "hover:bg-gray-50 dark:hover:bg-gray-800",
                location.pathname === '/auth' && searchParams.get('signup') === 'true' && !isDarkroomActive && "text-primary"
              )}
            >
              <UserPlus className={cn(
                "mb-1 h-5 w-5",
                isDarkroomActive 
                  ? "text-[#ea384c]" 
                  : location.pathname === '/auth' && searchParams.get('signup') === 'true'
                    ? "text-primary" 
                    : "text-gray-500 group-hover:text-primary"
              )} />
              <span className={cn(
                "text-xs truncate",
                isDarkroomActive 
                  ? "text-[#ea384c]" 
                  : location.pathname === '/auth' && searchParams.get('signup') === 'true'
                    ? "text-primary" 
                    : "text-gray-500 group-hover:text-primary"
              )}>
                {t('sign_up')}
              </span>
            </Link>
          )}
          
          {/* Profile/Sign In tab always shown */}
          <Link
            to={user ? "/profile" : "/auth"}
            onClick={handleNavigation(user ? "/profile" : "/auth")}
            className={cn(
              "group inline-flex flex-col items-center justify-center px-1",
              isDarkroomActive 
                ? "hover:bg-black" 
                : "hover:bg-gray-50 dark:hover:bg-gray-800",
              ((location.pathname === '/profile' || (location.pathname === '/auth' && !searchParams.get('signup'))) && !isDarkroomActive) && "text-primary"
            )}
          >
            <UserIcon className={cn(
              "mb-1 h-5 w-5",
              isDarkroomActive 
                ? "text-[#ea384c]" 
                : (location.pathname === '/profile' || (location.pathname === '/auth' && !searchParams.get('signup')))
                  ? "text-primary" 
                  : "text-gray-500 group-hover:text-primary"
            )} />
            <span className={cn(
              "text-xs truncate",
              isDarkroomActive 
                ? "text-[#ea384c]" 
                : (location.pathname === '/profile' || (location.pathname === '/auth' && !searchParams.get('signup')))
                  ? "text-primary" 
                  : "text-gray-500 group-hover:text-primary"
            )}>
              {user ? t('profile') : t('sign_in')}
            </span>
          </Link>
          
          {/* Admin tab only shown to admin users who are logged in */}
          {isAdmin && user && (
            <Link
              to="/admin"
              onClick={handleNavigation('/admin')}
              className={cn(
                "group inline-flex flex-col items-center justify-center px-1",
                isDarkroomActive 
                  ? "hover:bg-black" 
                  : "hover:bg-gray-50 dark:hover:bg-gray-800",
                location.pathname.startsWith('/admin') && !isDarkroomActive && "text-primary"
              )}
            >
              <ShieldAlert className={cn(
                "mb-1 h-5 w-5",
                isDarkroomActive 
                  ? "text-[#ea384c]" 
                  : location.pathname.startsWith('/admin') 
                    ? "text-primary" 
                    : "text-gray-500 group-hover:text-primary"
              )} />
              <span className={cn(
                "text-xs truncate",
                isDarkroomActive 
                  ? "text-[#ea384c]" 
                  : location.pathname.startsWith('/admin') 
                    ? "text-primary" 
                    : "text-gray-500 group-hover:text-primary"
              )}>
                {t('admin')}
              </span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
